interface SEOData {
    title?: string;
    description?: string;
    keywords?: string;
    ogImage?: string;
    ogUrl?: string;
    ogType?: string;
    twitterCard?: string;
    canonicalUrl?: string;
    noindex?: boolean;
}

// Base URL for the site
const BASE_URL = 'https://site.thisispenny.com';

// Default SEO data
const defaultSEO: SEOData = {
    title: 'Penny - Pension Management Made Simple',
    description: 'Penny helps you manage your pension with ease. Start your pension from just €1 a day.',
    keywords: 'pension, retirement, finance, savings, investment, pension management, retirement planning',
    ogImage: `${BASE_URL}/og-image.jpg`,
    ogUrl: BASE_URL,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    noindex: false,
};

// Route-specific SEO data
const routeSEO: Record<string, SEOData> = {
    '/': {
        title: 'Penny - Start Your Pension From Just €1 a Day',
        description: 'Penny makes pension management simple and accessible. Start your pension journey today with personalized recommendations.',
        keywords: 'pension, retirement, finance, savings, investment, pension management, retirement planning, pension calculator',
    },
    '/login': {
        title: 'Sign In to Your Penny Account',
        description: 'Access your Penny account to manage your pension and financial future.',
        keywords: 'pension login, retirement account, pension management, sign in',
    },
    '/signup': {
        title: 'Create Your Penny Account',
        description: 'Start your pension journey by creating a Penny account. Get personalized pension recommendations in minutes.',
        keywords: 'create pension account, pension signup, retirement planning, pension management',
    },
    '/pension-calculator': {
        title: 'Pension Calculator | Plan Your Retirement',
        description: 'Use our pension calculator to estimate your retirement income and plan for your financial future.',
        keywords: 'pension calculator, retirement calculator, retirement planning, pension estimation',
    },
    '/fact-find': {
        title: 'Pension Fact Find | Personalized Recommendations',
        description: 'Complete our fact find questionnaire to receive personalized pension recommendations tailored to your needs.',
        keywords: 'pension fact find, retirement questionnaire, personalized pension, pension recommendations',
    },
    '/my-profile': {
        title: 'Manage Your Penny Profile',
        description: 'Update your personal details and preferences to get the most out of your Penny account.',
        keywords: 'pension profile, account management, personal details, pension preferences',
        noindex: true, // Private page, should not be indexed
    },
    '/penny-space': {
        title: 'Penny Space | Your Pension Dashboard',
        description: 'Access your pension dashboard to view your pension status, contributions, and projected retirement income.',
        keywords: 'pension dashboard, retirement tracker, pension management, retirement planning',
    },
};

/**
 * Get SEO data for a specific route
 * @param path Current route path
 * @returns SEO data for the route
 */
export const getSeoDataForRoute = (path: string): SEOData => {
    // Check if we have specific SEO data for this route
    const routeData = routeSEO[path];

    if (routeData) {
        return {
            ...defaultSEO,
            ...routeData,
            ogUrl: `${BASE_URL}${path}`,
            canonicalUrl: `${BASE_URL}${path}`,
        };
    }

    // For dynamic routes, try to match patterns
    if (path.startsWith('/recommendations/details/')) {
        return {
            ...defaultSEO,
            title: 'Pension Recommendation Details | Penny',
            description: 'View detailed information about your personalized pension recommendation.',
            keywords: 'pension recommendation, retirement plan, pension details, personalized pension',
            ogUrl: `${BASE_URL}${path}`,
            canonicalUrl: `${BASE_URL}${path}`,
        };
    }

    if (path.startsWith('/pension-application/')) {
        return {
            ...defaultSEO,
            title: 'Pension Application | Penny',
            description: 'Complete your pension application process with Penny.',
            keywords: 'pension application, retirement setup, pension enrollment, pension registration',
            ogUrl: `${BASE_URL}${path}`,
            canonicalUrl: `${BASE_URL}${path}`,
            noindex: true, // Application process should be private
        };
    }

    // Return default SEO data with current path
    return {
        ...defaultSEO,
        ogUrl: `${BASE_URL}${path}`,
        canonicalUrl: `${BASE_URL}${path}`,
    };
};

/**
 * Generate structured data for the website
 * @returns JSON-LD structured data
 */
export const generateStructuredData = () => {
    const organizationData = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Penny',
        url: BASE_URL,
        logo: `${BASE_URL}/logo.png`,
        sameAs: [
            'https://twitter.com/pennyfs',
            'https://www.linkedin.com/company/pennyfs',
        ],
        contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+353-87-9119-440',
            contactType: 'customer service',
            availableLanguage: 'English',
        },
        description: 'Penny helps you manage your pension with ease. Start your pension from just €1 a day.',
    };

    return JSON.stringify(organizationData);
};

/**
 * Generate breadcrumb structured data
 * @param path Current route path
 * @param title Page title
 * @returns JSON-LD breadcrumb structured data
 */
export const generateBreadcrumbData = (path: string, title: string) => {
    const pathSegments = path.split('/').filter(Boolean);

    const breadcrumbItems = [
        {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: BASE_URL,
        },
    ];

    let currentPath = '';

    pathSegments.forEach((segment, index) => {
        currentPath += `/${segment}`;

        breadcrumbItems.push({
            '@type': 'ListItem',
            position: index + 2,
            name: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
            item: `${BASE_URL}${currentPath}`,
        });
    });

    const breadcrumbData = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems,
    };

    return JSON.stringify(breadcrumbData);
};